// Logique pure des écrans de la propreté : libellés, tons des états, semaine affichée, marqueurs et
// surfaces de la carte, résumé d'une réalisation. Testée seule.
import { ETATS_REALISATION, JOURS_SEMAINE, lundiDeLaSemaine, type EtatRealisation, type ProprietesCartePointCollecte, type ProprietesCarteSecteur } from '@cc/contrats';
import type { MarqueurCarte, TonEtat } from '@cc/ui';
import type { FeatureCollection, MultiPolygon, Polygon } from 'geojson';
import type { CarteProprete, Realisation, Tournee } from './types';

export const TON_REALISATION: Record<EtatRealisation, TonEtat> = { realisee: 'ok', partielle: 'attente', non_realisee: 'retard' };

export function libelleJour(jour: number): string {
  return JOURS_SEMAINE[jour] ?? `Jour ${jour}`;
}

export function aujourdhuiIso(maintenant = new Date()): string {
  const m = String(maintenant.getMonth() + 1).padStart(2, '0');
  const j = String(maintenant.getDate()).padStart(2, '0');
  return `${maintenant.getFullYear()}-${m}-${j}`;
}

/** Lundi de la semaine décalée de n semaines par rapport à celle du jour donné. */
export function semaineDecalee(jour: string, n: number): string {
  const lundi = lundiDeLaSemaine(jour);
  const d = new Date(`${lundi}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + 7 * n);
  return d.toISOString().slice(0, 10);
}

/** « lundi 19 octobre » : libellé court d'un jour du calendrier. */
export function libelleDate(jour: string): string {
  return new Date(`${jour}T00:00:00`).toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' });
}

/** Texte d'une occurrence du calendrier : heure, code et état de la réalisation (ou « à réaliser »). */
export function texteOccurrence(t: Pick<Tournee, 'heure' | 'code' | 'nom'>, r: Realisation | null, date: string, aujourdhui: string): string {
  const etat = r ? ETATS_REALISATION[r.etat] : date < aujourdhui ? 'Non renseignée' : 'À réaliser';
  return `${t.heure} ${t.code} · ${t.nom} : ${etat}`;
}

export function typeOccurrence(r: Realisation | null, date: string, aujourdhui: string): 'conseil' | 'echeance' | 'retard' | 'autre' {
  if (r?.etat === 'non_realisee') return 'retard';
  if (r?.etat === 'partielle') return 'echeance';
  if (r) return 'autre';
  return date < aujourdhui ? 'retard' : 'conseil';
}

/** Résumé des points d'une réalisation : « 3 points sur 4 collectés ». */
export function resumePoints(r: Pick<Realisation, 'points_collectes' | 'points_prevus' | 'etat'>): string {
  if (r.etat === 'non_realisee') return 'aucun point collecté';
  const total = r.points_prevus ?? r.points_collectes.length;
  return `${r.points_collectes.length} point${r.points_collectes.length > 1 ? 's' : ''} sur ${total} collecté${r.points_collectes.length > 1 ? 's' : ''}`;
}

export function marqueursPoints(c: CarteProprete | undefined, depart = 0): MarqueurCarte[] {
  return (c?.features ?? [])
    .filter((f) => f.geometry.type === 'Point' && f.properties.objet === 'point_collecte')
    .map((f, i) => {
      const p = f.properties as ProprietesCartePointCollecte;
      return {
        id: p.id,
        numero: depart + i + 1,
        longitude: (f.geometry as { coordinates: [number, number] }).coordinates[0],
        latitude: (f.geometry as { coordinates: [number, number] }).coordinates[1],
        libelle: `${p.code} · ${p.nom} · ${p.type_libelle} · ${p.frequence_hebdomadaire} passage${p.frequence_hebdomadaire > 1 ? 's' : ''} par semaine`,
        ton: 'ouvert' as const,
      };
    });
}

/** Surfaces à dessiner : les emprises des secteurs (polygones). */
export function surfacesSecteurs(c: CarteProprete | undefined): FeatureCollection<Polygon | MultiPolygon> {
  return {
    type: 'FeatureCollection',
    features: (c?.features ?? [])
      .filter((f) => f.geometry.type === 'Polygon')
      .map((f) => ({ type: 'Feature' as const, id: f.id, geometry: f.geometry as Polygon, properties: { numero: (f.properties as ProprietesCarteSecteur).code } })),
  };
}

export function montantFrancs(v: number): string {
  return `${v.toLocaleString('fr-FR')} F`;
}
